/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';

import styled from 'styled-components';
import hljs from 'highlight.js';
import json from 'highlight.js/lib/languages/json';
import { useSelector } from 'react-redux';
import { State } from '@store/index';

// import './light.css';

hljs.registerLanguage('json', json);

interface Props {
  language: string;
  code: any;
  noPadding?: boolean;
  backgroundColor?: string;
}

const Container = styled.div<{ noPadding?: boolean }>`
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-size: 1.3ch;
  padding: ${({ noPadding }) => (noPadding ? `0 0.75rem` : '0.75rem')};
  padding-right: 2rem;
  background-color: ${({ theme }) => theme.background.standard};

  /* Ensure that the <pre> tag does not break layout with horizontal scrolling */
  pre {
    overflow: auto;
  }

  /* Make sure the code has proper spacing, is not wrapped and is in a monospace font */
  code {
    text-align: left;
    white-space: pre;
    word-spacing: normal;
    word-break: normal;
    word-wrap: normal;
    background-color: ${({ theme }) => theme.background.standard};
    font-family: Monospace;
    width: 100%;

    line-height: 1.3;
  }

  /* Optional: Add line numbers */
  pre code {
    counter-reset: line;
  }

  pre code span {
    counter-increment: line;
  }

  pre code span::before {
    content: counter(line);
    display: inline-block;
    padding-right: 0.5em;
    margin-right: 0.5em;
    text-align: right;
    color: #888; /* Line number color */
    background-color: #eee; /* Line number background color */
    border-right: 1px solid #ddd; /* Line number separator */
  }

  /* Customize the appearance of the scrollbar in Webkit browsers */
  ::-webkit-scrollbar {
    height: 12px;
    width: 12px;
    background: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #bbb;
  }
`;

const Light = styled(Container)<{ backgroundColor?: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor || 'initial'};

  code {
    background-color: ${({ backgroundColor }) => backgroundColor || 'initial'};
    margin-bottom: 10px;
  }

  pre code.hljs {
    display: block;
    overflow-x: auto;
    padding: 1em;
  }
  code.hljs {
    padding: 3px 5px;
  }
  .hljs {
    color: #2f3337;
  }
  .hljs-subst {
    color: #2f3337;
  }
  .hljs-comment {
    color: #656e77;
  }
  .hljs-attr,
  .hljs-doctag,
  .hljs-keyword,
  .hljs-meta .hljs-keyword,
  .hljs-section,
  .hljs-selector-tag {
    color: #015692;
  }
  .hljs-attribute {
    color: #803378;
  }
  .hljs-name,
  .hljs-number,
  .hljs-quote,
  .hljs-selector-id,
  .hljs-template-tag,
  .hljs-type {
    color: #b75501;
  }
  .hljs-selector-class {
    color: #015692;
  }
  .hljs-link,
  .hljs-regexp,
  .hljs-selector-attr,
  .hljs-string,
  .hljs-symbol,
  .hljs-template-variable,
  .hljs-variable {
    color: #54790d;
  }
  .hljs-meta,
  .hljs-selector-pseudo {
    color: #015692;
  }
  .hljs-built_in,
  .hljs-literal,
  .hljs-title {
    color: #b75501;
  }
  .hljs-bullet,
  .hljs-code {
    color: #535a60;
  }
  .hljs-meta .hljs-string {
    color: #54790d;
  }
  .hljs-deletion {
    color: #c02d2e;
  }
  .hljs-addition {
    color: #2f6f44;
  }
  .hljs-emphasis {
    font-style: italic;
  }
  .hljs-strong {
    font-weight: 700;
  }
`;

const Dark = styled(Container)<{ backgroundColor?: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor || 'initial'};

  code {
    background-color: ${({ backgroundColor }) => backgroundColor || 'initial'};
    margin-bottom: 10px;
  }

  pre code.hljs {
    display: block;
    overflow-x: auto;
    padding: 1em;
  }
  code.hljs {
    padding: 3px 5px;
  }
  .hljs {
    color: #dcdcdc;
  }
  .hljs-keyword,
  .hljs-literal,
  .hljs-name,
  .hljs-symbol {
    color: #569cd6;
  }
  .hljs-link {
    color: #569cd6;
    text-decoration: underline;
  }
  .hljs-built_in,
  .hljs-type {
    color: #4ec9b0;
  }
  .hljs-class,
  .hljs-number {
    color: #b8d7a3;
  }
  .hljs-meta .hljs-string,
  .hljs-string {
    color: #d69d85;
  }
  .hljs-regexp,
  .hljs-template-tag {
    color: #9a5334;
  }
  .hljs-formula,
  .hljs-function,
  .hljs-params,
  .hljs-subst,
  .hljs-title {
    color: #dcdcdc;
  }
  .hljs-comment,
  .hljs-quote {
    color: #57a64a;
    font-style: italic;
  }
  .hljs-doctag {
    color: #608b4e;
  }
  .hljs-meta,
  .hljs-meta .hljs-keyword,
  .hljs-tag {
    color: #9b9b9b;
  }
  .hljs-template-variable,
  .hljs-variable {
    color: #bd63c5;
  }
  .hljs-attr,
  .hljs-attribute {
    color: #9cdcfe;
  }
  .hljs-section {
    color: gold;
  }
  .hljs-emphasis {
    font-style: italic;
  }
  .hljs-strong {
    font-weight: 700;
  }
  .hljs-bullet,
  .hljs-selector-attr,
  .hljs-selector-class,
  .hljs-selector-id,
  .hljs-selector-pseudo,
  .hljs-selector-tag {
    color: #d7ba7d;
  }
  .hljs-addition {
    background-color: #144212;
    display: inline-block;
    width: 100%;
  }
  .hljs-deletion {
    background-color: #600;
    display: inline-block;
    width: 100%;
  }
`;

export const Code: React.FC<Props> = ({ code, noPadding, backgroundColor }) => {
  const content = JSON.stringify(code, null, 2);
  const codeRef = useRef<HTMLElement>(null);

  const theme = useSelector((state: State) => state.flow.theme);

  useEffect(() => {
    if (codeRef.current) {
      codeRef.current.classList.remove('hljs');
      codeRef.current.removeAttribute('data-highlighted');

      codeRef.current.textContent = content;
      hljs.highlightElement(codeRef.current);
    }
  }, [theme, content]);

  if (theme === 'dark') {
    return (
      <Dark noPadding={noPadding} backgroundColor={backgroundColor}>
        <code ref={codeRef} className='json'>
          {content}
        </code>
      </Dark>
    );
  }

  return (
    <Light noPadding={noPadding} backgroundColor={backgroundColor}>
      <code ref={codeRef} className='json'>
        {content}
      </code>
    </Light>
  );
};
